import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { variant } from "styled-system";
import css from "@styled-system/css";
import { themeGet } from "@styled-system/theme-get";

const CroppedImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const CroppedImageInner = styled.div`
  padding-bottom: 100%;
  position: relative;
  width: 100%;
`;

const CroppedImageElement = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  height: auto;
  width: calc(100% + 0.5px);
`;

const resolveCustomSize = (customSize, props) => {
  if (!customSize) return null;

  if (
    customSize.includes("px") ||
    customSize.includes("rem") ||
    customSize.includes("em") ||
    customSize.includes("%")
  ) {
    return customSize;
  } else {
    return themeGet(`avatarScale.${customSize}`)(props) || customSize;
  }
};

const CroppedImage = ({
  src,
  alt,
  croppedAreaPercent,
  className,
  style,
  onError,
}) => {
  const scale = 100 / (croppedAreaPercent.width || 1);
  const transform = {
    x: `${-(croppedAreaPercent.x ?? 0) * scale}%`,
    y: `${-(croppedAreaPercent.y ?? 0) * scale}%`,
    scale,
  };

  const imageStyle = {
    transform: `translate3d(${transform.x}, ${transform.y}, 0) scale3d(${transform.scale}, ${transform.scale}, 1)`,
    width: "calc(100% + 0.5px)",
    height: "auto",
  };

  return (
    <CroppedImageContainer className={className} style={style}>
      <CroppedImageInner>
        <CroppedImageElement
          src={src}
          alt={alt}
          style={imageStyle}
          onError={onError}
        />
      </CroppedImageInner>
    </CroppedImageContainer>
  );
};

CroppedImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  croppedAreaPercent: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  onError: PropTypes.func,
};

export const CroppedImageWrapper = styled(CroppedImage)(
  (props) =>
    css({
      width:
        resolveCustomSize(props.customSize, props) ||
        themeGet("avatarScale.avatarDefault")(props),
      height:
        resolveCustomSize(props.customSize, props) ||
        themeGet("avatarScale.avatarDefault")(props),
      flex: "0 0 auto",
      backgroundColor: themeGet("colors.surface")(props),
      border: "solid 1px " + themeGet("colors.surfaceRaisedBorder")(props),
      display: "block",
      borderRadius: "50%",
      overflow: "hidden",
    }),
  (props) =>
    variant({
      prop: "sizing",
      variants: {
        small: {
          width: themeGet("avatarScale.avatarSmall")(props),
          height: themeGet("avatarScale.avatarSmall")(props),
        },
        large: {
          width: themeGet("avatarScale.avatarLarge")(props),
          height: themeGet("avatarScale.avatarLarge")(props),
        },
      },
    }),
  (props) =>
    variant({
      prop: "shape",
      variants: {
        square: {
          borderRadius: themeGet("radii.2")(props),
        },
        hexagon: {
          borderRadius: "0",
          clipPath:
            "polygon(50% 0, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%)",
        },
        tag: {
          borderRadius: "0",
          clipPath: "polygon(6% 0, 95% 0, 95% 65%, 50% 100%, 6% 65%)",
        },
      },
    })
);

export const HeaderAvatarCroppedImage = styled(CroppedImage)(
  ({ $size, $shape }) => ({
    width: $size,
    height: $size,
    transition: "width 200ms ease-in-out, height 200ms ease-in-out",
    borderRadius: $shape === "circle" || !$shape ? "50%" : "0",
    overflow: "hidden",
    display: "block",
  })
);

export default CroppedImage;
